import { Download, PlayArrow, Settings } from '@mui/icons-material';
import { ToggleButton } from '@mui/material';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { MarkItViewApi } from '../../../../Apis/Apis';
import { CalculateMatrixRequest, CalculateMatrixResponse, CommodityType, MarketViewProductModel, MatrixCalculationType, MatrixContractSettlement, MatrixStripType, MatrixTerm } from '../../../../Generated/Mark-It-View-Api';
import { CurrencyHelper } from '../../../../Helpers';
import { useLoadingState, useMtmDate } from '../../../../Hooks';
import { RootState, useApplicationSelector } from '../../../../Redux/ReduxStore';
import { UserAccessState } from '../../../../Redux/Slices/UserAccessSlice';
import { StoneXLoading, StoneXRow } from '../../../StoneX';
import MarkItViewCommodityTypeSelector from '../../../StoneX/MarkItViewCommodityTypeSelector/MarkItViewCommodityTypeSelector';
import MarkItViewProductSelector from '../../../StoneX/MarkItViewProductSelector.tsx/MarkItViewProductSelector';
import { StoneXButton, StoneXDatePicker, StoneXMultiselect, StoneXSelect, StoneXTextField, StoneXToggleButton, StoneXToggleButtonGroup } from '../../../StoneXMui';
import { exportMatrixToExcel } from './exportMatrixToExcel';
import MatrixChart from './MatrixChart';
import MatrixChartControls from './MatrixChartControls';
import {
  MatrixCalculationTypeLabels,
  MatrixContractSettlementLabels,
  MatrixReportConfiguration,
  MatrixStripTypeLabels,
  MatrixTermLabels,
  PriceOverlayType,
} from './MatrixLabels';
import MatrixSettlementTable from './MatrixSettlementTable';
import MatrixSpotRateComparisonTable from './MatrixSpotRateComparisonTable';
import MatrixTable from './MatrixTable';
import { narrowForwardCurveSeries, narrowGraphPoints, narrowResults, narrowYearPriceSeries } from './MatrixTypes';

interface MatrixReportProps {
  configuration?: string | null;
}

function parseConfiguration(configurationJson?: string | null): MatrixReportConfiguration {
  if (!configurationJson) return {};
  try { return JSON.parse(configurationJson) as MatrixReportConfiguration; }
  catch { return {}; }
}

export default function MatrixReport(props: MatrixReportProps) {
  const quoteDate = useMtmDate();
  const userAccess = useApplicationSelector((state: RootState): UserAccessState => state.userAccess);
  const isEmployee = userAccess.isEmployee;

  const configuration = useMemo(() => parseConfiguration(props.configuration), []);

  const [selectedCommodityType, setSelectedCommodityType] = useState<CommodityType>(CommodityType.Favorites);
  const [selectedProduct, setSelectedProduct] = useState<MarketViewProductModel | null>(null);

  const [startDate, setStartDate] = useState<Date | null>(quoteDate);
  const [stripType, setStripType] = useState<MatrixStripType>(MatrixStripType.RollingMonth);
  const [contractSettlement, setContractSettlement] = useState<MatrixContractSettlement>(MatrixContractSettlement.ExpirationDate);
  const [weighted, setWeighted] = useState<boolean>(configuration.defaults?.weighted ?? true);
  const [innerRange, setInnerRange] = useState<number>(configuration.defaults?.innerRange ?? 1);
  const [outerRange, setOuterRange] = useState<number>(configuration.defaults?.outerRange ?? 4);
  const [innerWeight, setInnerWeight] = useState<number>(configuration.defaults?.innerWeight ?? 20);
  const [outerWeight, setOuterWeight] = useState<number>(configuration.defaults?.outerWeight ?? 80);
  const [yearsBack, setYearsBack] = useState<number>(configuration.defaults?.yearsBack ?? 5);
  const [applyPpi, setApplyPpi] = useState<boolean>(true);
  const [aboveMedian, setAboveMedian] = useState<boolean>(false);
  const [selectedTerms, setSelectedTerms] = useState<MatrixTerm[]>(configuration.defaults?.terms ?? [MatrixTerm.Annual]);
  const [customMonths, setCustomMonths] = useState<number[]>([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
  const [startMonth, setStartMonth] = useState<number>(0);

  const [reportStartDate, setReportStartDate] = useState<Date | null>(dayjs(quoteDate).subtract(1, 'year').toDate());

  const [historyMode, setHistoryMode] = useState<boolean>(false);
  const [calculationDate, setCalculationDate] = useState<Date | null>(quoteDate);
  const [calcType, setCalcType] = useState<MatrixCalculationType>(MatrixCalculationType.Monthly);
  const [priceOverlay, setPriceOverlay] = useState<PriceOverlayType>(PriceOverlayType.None);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [selectedPercentiles, setSelectedPercentiles] = useState<number[]>([0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]);
  const [minPercentile, setMinPercentile] = useState<number | null>(null);
  const [maxPercentile, setMaxPercentile] = useState<number | null>(null);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState<boolean>(false);

  const [response, setResponse] = useState<CalculateMatrixResponse | null>(null);
  // Pinned when the report runs so the symbol always matches the plotted data, not a later product change.
  const [reportCurrencySymbol, setReportCurrencySymbol] = useState<string>('');

  // Narrow generated optional types to strict types for use in components and logic
  const results = useMemo(() => narrowResults(response?.results), [response]);
  const graphPoints = useMemo(() => narrowGraphPoints(response?.graphPoints), [response]);
  const yearPriceSeries = useMemo(() => narrowYearPriceSeries(response?.yearPriceSeries), [response]);
  const forwardCurveSeries = useMemo(() => narrowForwardCurveSeries(response?.forwardCurveSeries), [response]);

  const reportLoadingState = useLoadingState();

  const allTerms = Object.values(MatrixTerm);
  const allStripTypes = [
    MatrixStripType.FirstContractMonth,
    MatrixStripType.RollingMonth,
    MatrixStripType.SecondContractMonth,
    MatrixStripType.Strip,
    MatrixStripType.ContractPeriod,
  ];
  const allSettlements = Object.values(MatrixContractSettlement);
  const allCalcTypes = Object.values(MatrixCalculationType);
  const allMonths = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  const monthLabels: Record<number, string> = { 1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'May', 6: 'Jun', 7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec' };

  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: 6 }, (_, i) => currentYear + i);



  function runReport() {
    if (!selectedProduct) return;
    if (historyMode && !calculationDate) return;
    if (!historyMode && !startDate) return;

    const effectiveStartDate = (historyMode || priceOverlay === PriceOverlayType.Daily)
      ? dayjs(reportStartDate ?? dayjs(historyMode ? calculationDate : startDate).subtract(1, 'year'))
      : dayjs(startDate);

    const request: CalculateMatrixRequest = {
      productId: selectedProduct.productId,
      startDate: effectiveStartDate.format('YYYY-MM-DD'),
      matrixStripType: stripType,
      contractSettlement: contractSettlement,
      weighted: weighted,
      innerRange: innerRange,
      outerRange: outerRange,
      innerWeight: innerWeight / 100,
      outerWeight: outerWeight / 100,
      yearsBack: weighted ? undefined : yearsBack,
      applyPpi: applyPpi,
      aboveMedian: aboveMedian,
      terms: selectedTerms,
      months: selectedTerms.includes(MatrixTerm.Custom) ? customMonths : undefined,
      calculationDate: historyMode ? dayjs(calculationDate).format('YYYY-MM-DD') : priceOverlay === PriceOverlayType.Daily ? dayjs(startDate).format('YYYY-MM-DD') : undefined,
      calculationType: historyMode ? calcType : undefined,
      selectedYears: selectedYears.length > 0 && priceOverlay === PriceOverlayType.Daily ? selectedYears : undefined,
      startMonth: startMonth > 0 ? startMonth : undefined,
      includeForwardCurve: priceOverlay === PriceOverlayType.LastSettlement ? true : undefined,
    };

    MarkItViewApi.calculateMatrix(reportLoadingState.setLoadingState, request)
      .then((res) => {
        setResponse(res.data);
        setReportCurrencySymbol(CurrencyHelper.getSymbol(selectedProduct.currency));
      })
      .catch(() => setResponse(null));
  }

  function exportToExcel() {
    if (!response || !selectedProduct) { return; }
    exportMatrixToExcel({
      response,
      productName: selectedProduct.name ?? 'Unknown',
      results,
      weighted,
      contractSettlement,
      innerRange,
      outerRange,
      innerWeight,
      outerWeight,
      stripType,
      yearsBack,
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Row 1: Quote Date, Product selection, History */}
      <StoneXRow>
        {historyMode ? (
          <StoneXDatePicker value={calculationDate} onChange={setCalculationDate} label="Quote Date" />
        ) : (
          <StoneXDatePicker value={startDate} onChange={setStartDate} label="Quote Date" />
        )}
        <MarkItViewCommodityTypeSelector
          value={selectedCommodityType}
          onChange={(x) => setSelectedCommodityType(x!)}
        />
        <MarkItViewProductSelector
          commodityType={selectedCommodityType}
          onProductChange={setSelectedProduct}
        />
      </StoneXRow>

      {/* Row 2: Strip type, settlement, terms */}
      <StoneXRow align='start'>
        <StoneXSelect
          value={stripType}
          onChange={(value) => setStripType(value ?? MatrixStripType.RollingMonth)}
          options={allStripTypes}
          getOptionLabel={(x) => MatrixStripTypeLabels[x]}
          getId={(x) => x}
          label="Strip Type"
        />
        <StoneXSelect
          value={contractSettlement}
          onChange={(value) => setContractSettlement(value ?? MatrixContractSettlement.ExpirationDate)}
          options={allSettlements}
          getOptionLabel={(x) => MatrixContractSettlementLabels[x]}
          getId={(x) => x}
          label="Contract Settlement"
        />
        <StoneXMultiselect
          list={allTerms}
          labelSelector={(x: MatrixTerm) => MatrixTermLabels[x]}
          valueSelector={(x: MatrixTerm) => x}
          resultSetter={setSelectedTerms}
          initial={selectedTerms}
          label="Terms"
        />
        {selectedTerms.includes(MatrixTerm.Custom) && (
          <StoneXMultiselect
            list={allMonths}
            labelSelector={(x: number) => monthLabels[x]}
            valueSelector={(x: number) => x}
            resultSetter={setCustomMonths}
            initial={customMonths}
            label="Custom Months"
          />
        )}
        {selectedTerms.includes(MatrixTerm.Custom) && customMonths.length === 12 && (
          <StoneXSelect
            value={startMonth}
            onChange={(value) => setStartMonth(value ?? 0)}
            options={[0, ...allMonths]}
            getOptionLabel={(x) => x === 0 ? 'Calendar Year' : monthLabels[x]}
            getId={(x) => x.toString()}
            label="Fiscal Year Start"
          />
        )}
      </StoneXRow>

      {/* Row 3: History and Price Overlay */}
      <StoneXRow>
        {isEmployee && <StoneXToggleButton label="History" value={historyMode} onChange={(value) => { setHistoryMode(value); setResponse(null); if (value && priceOverlay === PriceOverlayType.LastSettlement) { setPriceOverlay(PriceOverlayType.Daily); } }} disabled={reportLoadingState.isLoading()} />}
        <StoneXToggleButtonGroup
          value={priceOverlay}
          exclusive
          onChange={(_, value) => { if (value != null) { setPriceOverlay(value); } }}
          size="small"
          label="Price Overlay"
        >
          <ToggleButton value={PriceOverlayType.None}>None</ToggleButton>
          <ToggleButton value={PriceOverlayType.Daily}>Daily</ToggleButton>
          <ToggleButton value={PriceOverlayType.LastSettlement} disabled={historyMode}>Settlement</ToggleButton>
        </StoneXToggleButtonGroup>
        {priceOverlay === PriceOverlayType.Daily && (
          <StoneXMultiselect
            list={availableYears}
            labelSelector={(x: number) => x.toString()}
            valueSelector={(x: number) => x}
            resultSetter={setSelectedYears}
            initial={selectedYears}
            label="Contract Years"
          />
        )}
      </StoneXRow>

      {/* Advanced Options toggle */}
      {isEmployee && (
        <StoneXRow>
          <StoneXButton onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} variant="text" startIcon={<Settings />}>
            {showAdvancedOptions ? 'Hide Advanced Options' : 'Show Advanced Options'}
          </StoneXButton>
        </StoneXRow>
      )}

      {/* Advanced Options */}
      {showAdvancedOptions && (
        <>
          <StoneXRow>
            {historyMode && (
              <StoneXSelect
                value={calcType}
                onChange={(value) => setCalcType(value ?? MatrixCalculationType.Monthly)}
                options={allCalcTypes}
                getOptionLabel={(x) => MatrixCalculationTypeLabels[x]}
                getId={(x) => x}
                label="Calculation Type"
              />
            )}
            <StoneXToggleButton label="Weighted" value={weighted} onChange={setWeighted} />
            <StoneXToggleButton label="Apply PPI" value={applyPpi} onChange={setApplyPpi} />
            <StoneXToggleButton label="Above Median" value={aboveMedian} onChange={setAboveMedian} />
          </StoneXRow>
          <StoneXRow>
            {weighted ? (
              <>
                <StoneXTextField label="Inner Range (years)" type="number" value={innerRange} onUpdate={(value) => setInnerRange(Number(value))} width="small" />
                <StoneXTextField label="Outer Range (years)" type="number" value={outerRange} onUpdate={(value) => setOuterRange(Number(value))} width="small" />
                <StoneXTextField label="Inner Weight (%)" type="number" value={innerWeight} onUpdate={(value) => setInnerWeight(Number(value))} width="small" />
                <StoneXTextField label="Outer Weight (%)" type="number" value={outerWeight} onUpdate={(value) => setOuterWeight(Number(value))} width="small" />
              </>
            ) : (
              <StoneXTextField label="Years Back" type="number" value={yearsBack} onUpdate={(value) => setYearsBack(Number(value))} width="small" />
            )}
          </StoneXRow>
          {(historyMode || priceOverlay === PriceOverlayType.Daily) && (
            <StoneXRow>
              <StoneXDatePicker value={reportStartDate} onChange={setReportStartDate} label="Start Date" />
            </StoneXRow>
          )}
        </>
      )}

      {/* Run button */}
      <StoneXRow>
        <StoneXButton onClick={runReport} disabled={!selectedProduct || reportLoadingState.isLoading()} startIcon={<PlayArrow />}>
          Run Matrix
        </StoneXButton>
        {response && !reportLoadingState.isLoading() && (
          <StoneXButton onClick={exportToExcel} variant="outlined" startIcon={<Download />}>
            Export to Excel
          </StoneXButton>
        )}
      </StoneXRow>

      {/* Results */}
      <StoneXLoading show={reportLoadingState.isLoading()} />

      {response && !reportLoadingState.isLoading() && (
        <>
          {/* Tables */}
          {historyMode ? (
            <>
              <MatrixTable
                results={results}
                graphPoints={graphPoints}
                weighted={weighted}
                historyMode={historyMode}
                selectedPercentiles={selectedPercentiles}
              />
              <MatrixSpotRateComparisonTable
                graphPoints={graphPoints}
                yearPriceSeries={yearPriceSeries}
                results={results}
                selectedPercentiles={selectedPercentiles}
              />
            </>
          ) : (
            <>
              <>
                <MatrixTable
                  results={results}
                  graphPoints={graphPoints}
                  weighted={weighted}
                  historyMode={historyMode}
                  selectedPercentiles={selectedPercentiles}
                />
              </>
              {priceOverlay === PriceOverlayType.Daily && (
                <>
                  <MatrixSpotRateComparisonTable
                    graphPoints={graphPoints}
                    yearPriceSeries={yearPriceSeries}
                    results={results}
                    selectedPercentiles={selectedPercentiles}
                  />
                </>
              )}
              {priceOverlay === PriceOverlayType.LastSettlement && (
                <>
                  <MatrixSettlementTable
                    results={results}
                    forwardCurveSeries={forwardCurveSeries}
                    selectedPercentiles={selectedPercentiles}
                  />
                </>
              )}
            </>
          )}

          {/* Decile band controls - presets + custom */}
          <MatrixChartControls
            selectedPercentiles={selectedPercentiles}
            onPercentilesChange={setSelectedPercentiles}
            minPercentile={minPercentile}
            onMinPercentileChange={setMinPercentile}
            maxPercentile={maxPercentile}
            onMaxPercentileChange={setMaxPercentile}
          />

          {/* Chart percentile gauge (single day) or area+line chart (history) */}
          {graphPoints.length > 0 && (
            <MatrixChart
              graphPoints={graphPoints}
              yearPriceSeries={yearPriceSeries}
              forwardCurveSeries={forwardCurveSeries}
              selectedPercentiles={selectedPercentiles}
              minPercentile={minPercentile}
              maxPercentile={maxPercentile}
              historyMode={historyMode}
              currencySymbol={reportCurrencySymbol}
            />
          )}
        </>
      )}
    </div>
  );
}
